import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { API_BASE } from './api'

/**
 * Fila do Segundo Par de Olhos (dono, 25/08 — "faça 4").
 *
 * O revisor por IA relê cada documento guardado e grava veredito + citação
 * LITERAL, que o backend confere contra o texto (sql/071). Esta tela lista o
 * que pede olho humano: veredito "errado"/"parcial", ou citação sem lastro.
 *
 * O julgamento do dono aqui vira GABARITO permanente (sql/072) — a fonte de
 * verdade que calibra o revisor e nunca é sobrescrita por máquina. Julgou →
 * sai da fila (o gabarito responde por ele dali em diante).
 */

type ItemFila = {
  codigo: string
  nome: string
  tipo: string
  url_origem: string
  revisao_veredito: 'certo' | 'errado' | 'parcial'
  revisao_motivo: string | null
  revisao_citacao: string | null
  revisao_lastro: boolean | null
  revisao_em: string
  gabarito: string | null
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`)
  return r.json()
}

const COR_VEREDITO: Record<string, string> = {
  errado: 'bg-red-100 text-red-800',
  parcial: 'bg-amber-100 text-amber-800',
  certo: 'bg-emerald-100 text-emerald-800',
}

function BotoesJulgar({ item, onJulgado }: { item: ItemFila; onJulgado: () => void }) {
  const [obs, setObs] = useState('')
  const julgar = useMutation({
    mutationFn: (veredito: string) =>
      apiFetch('/api/revisao/julgar', {
        method: 'POST',
        body: JSON.stringify({ codigo: item.codigo, url_origem: item.url_origem, veredito, obs: obs || null }),
      }),
    onSuccess: onJulgado,
  })
  return (
    <div className="flex items-center gap-2">
      <input
        value={obs}
        onChange={(e) => setObs(e.target.value)}
        placeholder="obs (opcional)"
        className="border rounded px-2 py-1 text-xs w-40"
      />
      {(['certo', 'errado', 'parcial'] as const).map((v) => (
        <button
          key={v}
          onClick={() => julgar.mutate(v)}
          disabled={julgar.isPending}
          className={`px-2 py-1 rounded text-xs font-medium border hover:opacity-80 ${COR_VEREDITO[v]}`}
        >
          {v}
        </button>
      ))}
    </div>
  )
}

export default function RevisaoFila() {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['revisao-fila'],
    queryFn: () => apiFetch<{ fila: ItemFila[] }>('/api/revisao/fila'),
  })

  if (isLoading) return <div className="p-6 text-slate-500">Carregando a fila…</div>
  if (error) return <div className="p-6 text-red-600">Erro: {(error as Error).message}</div>

  // Já julgado pelo dono = resolvido; o gabarito responde por ele.
  const fila = (data?.fila ?? []).filter((i) => !i.gabarito)
  const julgados = (data?.fila ?? []).length - fila.length

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <div className="flex items-baseline justify-between mb-1">
        <h1 className="text-xl font-bold text-slate-800">Revisão de documentos</h1>
        <span className="text-sm text-slate-500">
          {fila.length} aguardando seu julgamento{julgados > 0 && ` · ${julgados} já julgados`}
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        O revisor por IA relê cada documento guardado e aponta o que merece olho humano.
        Seu julgamento vira gabarito permanente — calibra o revisor e nunca é sobrescrito.
      </p>

      {fila.length === 0 ? (
        <div className="rounded border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 text-sm">
          Fila vazia — nenhum documento aguardando conferência.
        </div>
      ) : (
        <div className="space-y-3">
          {fila.map((i) => (
            <div key={`${i.codigo}-${i.url_origem}`} className="rounded border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${COR_VEREDITO[i.revisao_veredito]}`}>
                  {i.revisao_veredito}
                </span>
                {i.revisao_lastro === false && (
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                    citação sem lastro
                  </span>
                )}
                <Link to={`/produto/${encodeURIComponent(i.codigo)}`} className="font-medium text-blue-700 hover:underline">
                  [{i.codigo}] {i.nome}
                </Link>
              </div>
              <div className="mt-1 text-sm text-slate-700">{i.revisao_motivo}</div>
              {i.revisao_citacao && (
                <div className="mt-1 text-xs text-slate-500 italic">citou: “{i.revisao_citacao}”</div>
              )}
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <a href={i.url_origem} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all">
                  {i.url_origem}
                </a>
                <BotoesJulgar item={i} onJulgado={() => qc.invalidateQueries({ queryKey: ['revisao-fila'] })} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
